import { useInjectReducer } from "utils/@reduxjs/useInjectReducer";
import { talentAgenciesSaga } from "./saga";
import { useInjectSaga } from "utils/@reduxjs/useInjectSaga";
import { createSlice } from "@reduxjs/toolkit";
import { useMemo } from "react";

export const initialState = {
  talentAgencyList: {
    initialLoading: true,
    loading: false,
    error: null,
    agencies: [],
    total_agencies: "",
    leads_opted_counts: {},
    pagination: ""
  },
  talentAgencyMembers: {
    initialLoading: true,
    loading: false,
    error: null,
    creators: [],
    agency_details: {},
    manager_dropdown: [],
    teamMembers: [],
    pagination: ""
  },
  managerDropdown: {
    initialLoading: true,
    loading: false,
    error: null,
    managers: []
  },
  addManager: {
    initialLoading: true,
    loading: false,
    error: null,
    success: false
  },
  updateMember: {
    loading: false,
    error: null,
    success: false
  },
  removeMember: {
    loading: false,
    status: null,
    data: null,
    error: null
  },
  managerList: {
    loading: false,
    error: null,
    managers: []
  }
};

export const TalentAgenciesSlice = createSlice({
  name: "talentAgencies",
  initialState,
  reducers: {
    // --- Fetch Agency List ---
    fetchTalentAgencyList: (state) => {
      state.talentAgencyList.loading = true;
      if (state.talentAgencyList.initialLoading) {
        state.talentAgencyList.initialLoading = true;
      }
      state.talentAgencyList.error = null;
    },
    fetchTalentAgencyListSuccess: (state, action) => {
      const { agencies, pagination, total_agencies, leads_opted_counts } =
        action.payload;
      state.talentAgencyList.loading = false;
      state.talentAgencyList.initialLoading = false;
      state.talentAgencyList.error = null;
      state.talentAgencyList.agencies = agencies || [];
      state.talentAgencyList.total_agencies = total_agencies || [];
      state.talentAgencyList.leads_opted_counts = leads_opted_counts || {};
      state.talentAgencyList.pagination = pagination || [];
    },
    fetchTalentAgencyListError: (state, action) => {
      state.talentAgencyList.loading = false;
      state.talentAgencyList.initialLoading = false;
      state.talentAgencyList.error = action.payload;
    },

    // === FETCH AGENCY MEMBERS (CREATORS / MANAGERS) ===
    fetchTalentAgencyMembers: (state) => {
      state.talentAgencyMembers.loading = true;
      if (state.talentAgencyMembers.initialLoading) {
        state.talentAgencyMembers.initialLoading = true;
      }
      state.talentAgencyMembers.error = null;
    },
    fetchTalentAgencyMembersSuccess: (state, action) => {
      const {
        type,
        creators,
        teamMembers,
        agency_details,
        pagination,
        manager_dropdown
      } = action.payload;
      state.talentAgencyMembers.loading = false;
      state.talentAgencyMembers.error = null;
      state.talentAgencyMembers.initialLoading = false;

      // Dynamically store data based on `type`
      if (type === "creators") {
        state.talentAgencyMembers.creators = creators || [];
      } else if (type === "team_members") {
        state.talentAgencyMembers.teamMembers = teamMembers || [];
      }

      state.talentAgencyMembers.agency_details = agency_details || {};
      state.talentAgencyMembers.manager_dropdown = manager_dropdown || [];
      state.talentAgencyMembers.pagination = pagination || {};
    },
    fetchTalentAgencyMembersError: (state, action) => {
      state.talentAgencyMembers.loading = false;
      state.talentAgencyMembers.error = action.payload;
      state.talentAgencyMembers.initialLoading = false;
    },

    // --- Manager Dropdown ---
    fetchManagerDropdown: (state) => {
      state.managerDropdown.loading = true;
      state.managerDropdown.error = null;
    },

    fetchManagerDropdownSuccess: (state, action) => {
      state.managerDropdown.loading = false;
      state.managerDropdown.initialLoading = false;
      state.managerDropdown.error = null;
      state.managerDropdown.managers = action.payload || []; // array directly
    },

    fetchManagerDropdownError: (state, action) => {
      state.managerDropdown.loading = false;
      state.managerDropdown.initialLoading = false;
      state.managerDropdown.error = action.payload;
    },

    // talent-agency-manager
    addTalentAgencyManager: (state) => {
      state.addManager.loading = true;
      state.addManager.error = null;
      state.addManager.success = false;
    },

    addTalentAgencyManagerSuccess: (state, action) => {
      const newMember = action.payload;

      state.addManager.loading = false;
      state.addManager.initialLoading = false;
      state.addManager.error = null;
      state.addManager.success = true;

      // ADD (append), NOT replace
      state.talentAgencyMembers.teamMembers = [
        ...state.talentAgencyMembers.teamMembers,
        {
          id: newMember.signal_user_id,
          name: newMember.name,
          phone_number: newMember.phone_number,
          email: newMember.email,
          role: newMember.role === "admin" ? "admin" : "standard",
          creator_manager: newMember.talent_manager_id ? "Yes" : "No",
          creators_managed: 0,
          talent_manager_id: newMember.talent_manager_id
        }
      ];

      // Update total_team_members count
      state.talentAgencyMembers.agency_details = {
        ...state.talentAgencyMembers.agency_details,
        total_team_members:
          (state.talentAgencyMembers.agency_details.total_team_members || 0) +
          1,
        total_team_members_filtered:
          (state.talentAgencyMembers.agency_details
            .total_team_members_filtered || 0) + 1
      };
    },

    addTalentAgencyManagerError: (state, action) => {
      state.addManager.loading = false;
      state.addManager.initialLoading = false;
      state.addManager.error = action.payload;
      state.addManager.success = false;
    },

    resetAddManagerStatus: (state) => {
      state.addManager.loading = false;
      state.addManager.error = null;
      state.addManager.success = false;
    },

    // --- Update Team Member ---
    updateTeamMember: (state) => {
      state.updateMember.loading = true;
      state.updateMember.error = null;
      state.updateMember.success = false;
    },

    updateTeamMemberSuccess: (state, action) => {
      state.updateMember.loading = false;
      state.updateMember.success = true;

      const updatedMember = action.payload;

      state.talentAgencyMembers.teamMembers =
        state.talentAgencyMembers.teamMembers.map((member) =>
          member.id === updatedMember.id
            ? { ...member, ...updatedMember }
            : member
        );
    },

    updateTeamMemberError: (state, action) => {
      state.updateMember.loading = false;
      state.updateMember.error = action.payload;
    },

    resetUpdateMemberStatus: (state) => {
      state.updateMember.loading = false;
      state.updateMember.error = null;
      state.updateMember.success = false;
    },

    // REMOVE TEAM MEMBER
    removeTeamMember: (state) => {
      state.removeMember.loading = true;
      //  RESET HERE
      state.removeMember.status = null;
      state.removeMember.error = null;
    },

    removeTeamMemberSuccess: (state, action) => {
      state.removeMember.loading = false;
      state.removeMember.status = action.payload.success;
      state.removeMember.data = action.payload;

      // Do NOT remove from UI if reassignment is required
      if (action.payload.success !== true) return;

      const removedId = action.payload.userId;

      state.talentAgencyMembers.teamMembers =
        state.talentAgencyMembers.teamMembers.filter(
          (member) => member.id !== removedId
        );
    },

    removeTeamMemberError: (state, action) => {
      state.removeMember.loading = false;
      state.removeMember.error = action.payload;
    },

    // --- FETCH MANAGER LIST (REASSIGN) ---
    fetchManagersList: (state) => {
      state.managerList.loading = true;
      state.managerList.error = null;
    },

    fetchManagersListSuccess: (state, action) => {
      state.managerList.loading = false;
      state.managerList.managers = action.payload || [];
    },

    fetchManagersListError: (state, action) => {
      state.managerList.loading = false;
      state.managerList.error = action.payload;
    },

    // REASSIGN CREATORS TO NEW MANAGER
    reassignCreatorsToManager: (state) => {
      state.removeMember.loading = true;
      state.removeMember.error = null;
      state.removeMember.status = null;
    },

    reassignCreatorsToManagerSuccess: (state, action) => {
      state.removeMember.loading = false;
      state.removeMember.status = true;
      state.removeMember.data = action.payload;

      // remove deleted manager from UI
      state.talentAgencyMembers.teamMembers =
        state.talentAgencyMembers.teamMembers.filter(
          (m) => m.talent_manager_id !== action.payload.from_manager_id
        );
    },

    reassignCreatorsToManagerError: (state, action) => {
      state.removeMember.loading = false;
      state.removeMember.error = action.payload;
    }
  }
});

export const useTalentAgenciesSlice = () => {
  useInjectReducer(TalentAgenciesSlice.name, TalentAgenciesSlice.reducer);
  useInjectSaga(TalentAgenciesSlice.name, talentAgenciesSaga);
  const actions = useMemo(() => TalentAgenciesSlice.actions, []);
  return { actions };
};

export const { actions: TalentAgenciesActions } = TalentAgenciesSlice;
export default TalentAgenciesSlice.reducer;
